import { useEffect, useMemo, useState } from "react";
import { PointerSensor, useSensor, useSensors, closestCorners, pointerWithin, } from "@dnd-kit/core";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { callautopopulate } from "../../../services/callautopop";
import { useCommon } from "../../Common/useCommon";
import _ from "underscore";

export const useKanbanBoard = () => {
    const { projectOpts, getUserProjects } = useCommon();
    const [columns, setColumns] = useState({});
    const [activeId, setActiveId] = useState(null);
    const [activeTask, setActiveTask] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedCardIds, setSelectedCardIds] = useState([]);
    const [tempData, setTempData] = useState([]);
    const [option, setOptions] = useState({});
    const [loader, setLoader] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});
    const [filterValue, setFilterValue] = useState({
        user: [],
        search: "",
        groups: "",
        isChange: false
    })

    useEffect(() => {
        setOptions((prev) => ({ ...prev, projects: projectOpts }));
    }, [projectOpts])

    useEffect(() => {
        callautopopulate("agile_groups").then(async res => {
            setOptions((prev) => ({ ...prev, agileGroups: res?.agile_groups }))
            getUserProjects()
        })
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 10 } })
    );

    const customCollisionDetection = (args) => {
        const pointerCollisions = pointerWithin(args);
        if (pointerCollisions.length > 0) return pointerCollisions;
        return closestCorners(args);
    };

    const findColumn = (taskId) => {
        for (const [colKey, col] of Object.entries(columns)) {
            const idx = col.items.findIndex((item) => item.id === taskId);
            if (idx !== -1) return { colKey, idx };
        }
        return { colKey: null, idx: -1 };
    };

    function handleDragStart(event) {
        if (selectMode) return;
        setActiveId(event.active.id);
        const { colKey, idx } = findColumn(event.active.id);
        if (colKey !== null && idx !== -1) {
            setActiveTask(columns[colKey].items[idx]);
        }
    }
    
    function handleDragEnd(event) {
        if (selectMode) return;
        const { active, over } = event;
        setActiveId(null);
        setActiveTask(null);
        if (!over) return;
        if (columns[over.id]) {
            const { colKey: fromCol, idx: fromIdx } = findColumn(active.id);
            if (fromCol !== over.id) {
                const item = columns[fromCol].items[fromIdx];
                setColumns((prev) => {
                    const newFrom = [...prev[fromCol].items];
                    newFrom.splice(fromIdx, 1);
                    const newTo = [...prev[over.id].items, item];
                    return {
                        ...prev,
                        [fromCol]: { ...prev[fromCol], items: newFrom },
                        [over.id]: { ...prev[over.id], items: newTo },
                    };
                });
            }
            return;
        }
        const { colKey: fromCol, idx: fromIdx } = findColumn(active.id);
        const { colKey: toCol, idx: toIdx } = findColumn(over.id);
        if (fromCol && toCol) {
            if (fromCol === toCol) {
                setColumns((prev) => {
                    const newItems = [...prev[fromCol].items];
                    const [moved] = newItems.splice(fromIdx, 1);
                    newItems.splice(toIdx, 0, moved);
                    return {
                        ...prev,
                        [fromCol]: { ...prev[fromCol], items: newItems },
                    };
                });
            } else {
                setColumns((prev) => {
                    const fromItems = [...prev[fromCol].items];
                    const [moved] = fromItems.splice(fromIdx, 1);
                    const toItems = [...prev[toCol].items];
                    toItems.splice(toIdx, 0, moved);
                    return {
                        ...prev,
                        [fromCol]: { ...prev[fromCol], items: fromItems },
                        [toCol]: { ...prev[toCol], items: toItems },
                    };
                });
            }
        }
    }

    function moveSelectedCardsToColumn(targetColKey) {
        if (!selectMode || selectedCardIds.length === 0) return;

        setColumns((prev) => {
            const newColumns = {};
            for (const [colKey, col] of Object.entries(prev)) {
                newColumns[colKey] = {
                    ...col,
                    items: col.items.filter((item) => !selectedCardIds.includes(item.id)),
                };
            }
            const selectedCards = [];
            for (const [colKey, col] of Object.entries(prev)) {
                for (const item of col.items) {
                    if (selectedCardIds.includes(item.id)) {
                        selectedCards.push(item);
                    }
                }
            }
            newColumns[targetColKey].items = [
                ...newColumns[targetColKey].items,
                ...selectedCards,
            ];
            return newColumns;
        });

        setSelectedCardIds([]);
        setSelectMode(false);
    }

    const getKanbanDetails = async () => {
        try {
            setLoader(true)
            const payload = {
                "project_ids": _.pluck(filterValue?.projects, "project_id")?.toString(),
                "bucket_ids": "",
                "sprint_ids": "",
                "issue_type_ids": "",
                "epic_ids": _.pluck(filterValue?.epic, "issue_id")?.toString() || ""

            }
            const res = await services.getInvokeRemote("POST", Urls("GETKANBANBOARDVIEWDETAILS"), payload);
            if (!res?.body?.error_status) {
                const data = res?.body?.data?.kanban_board_details
                const groupedByStatus = data?.reduce((acc, item) => {
                    const status = item.status_name || "";
                    if (!acc[status]) {
                        acc[status] = { name: status, items: [] };
                    }
                    acc[status].items.push({
                        id: item.issue_id,
                        parent: item.feature_issue_summary,
                        content: item.issue_summary,
                        feature_issue_key: item.feature_issue_key || null,
                        priority: item.priority_name,
                        complexity: item.complexity,
                        actualHours: item.loged_hours,
                        totalHours: item.planned_estimate_hours,
                        image_url: item.image_url,
                        user: item?.declare_user_name,
                        user_code: item?.user_code,
                        issueKey: item.issue_key,
                        issue_type_image_url: item?.issue_type_image_url,
                        project_name: item?.project_name
                    });

                    return acc;
                }, {});

                const uniqueUsers = data
                    .filter(
                        (user, index, self) =>
                            user.user_code && index === self.findIndex((u) => u.user_code === user.user_code)
                    )
                    .map(({ user_code, declare_user_name, image_url }) => ({
                        user_code,
                        user_name: declare_user_name,
                        image_url,
                    }));

                setOptions((prev) => ({
                    ...prev,
                    userList: uniqueUsers
                }))
                setColumns(groupedByStatus)
                setTempData(groupedByStatus)
            }
            setLoader(false)
        } catch (error) {
            setLoader(false)
            console.error("error:::::", error)
        }
    }

    useEffect(() => {
        getKanbanDetails()
    }, [])


    useEffect(() => {
        if (!filterValue?.search?.trim() && (filterValue?.user?.length === 0) && filterValue.groups?.group_key === "none" && filterValue.groups?.group_key === "stories") {
            setColumns(tempData);
            return;
        }

        const lowerQuery = filterValue?.search?.toLowerCase();
        const allGroupNames = Object.keys(tempData);
        const filtered = {};

        allGroupNames.forEach(colKey => {
            const col = tempData[colKey];
            const filteredItems = col.items.filter((item) => {
                const matchesSearch =
                    (item.content && item.content.toLowerCase().includes(lowerQuery)) ||
                    (item.parent && item.parent.toLowerCase().includes(lowerQuery)) ||
                    (item.issueKey && item.issueKey.toLowerCase().includes(lowerQuery)) ||
                    (item.user && item.user.toLowerCase().includes(lowerQuery));

                const matchesUser = filterValue?.user?.length === 0 || filterValue?.user?.includes(item.user_code);
                return matchesSearch && matchesUser;
            });

            filtered[colKey] = { ...col, items: filteredItems };
        });

        setColumns(filtered);
    }, [filterValue]);

    const normalizedColumns = useMemo(() => {
        const unassignedKey = "Unassigned";
        function fillAndOrderGroups(userGroup, allGroupNames) {
            allGroupNames.forEach((groupName) => {
                if (!userGroup.groups[groupName]) {
                    userGroup.groups[groupName] = { name: groupName, items: [] };
                }
            });
            const orderedGroups = {};
            allGroupNames?.sort()?.forEach((groupName) => {
                orderedGroups[groupName] = userGroup.groups[groupName];
            });
            userGroup.groups = orderedGroups;
        }

        if (filterValue?.groups?.group_key && filterValue.groups?.group_key !== "none" && filterValue.groups?.group_key !== "stories") {
            const groupedByUserThenStatus = {};
            const allGroupNames = Object.keys(columns);
            Object.values(columns).forEach((col) => {
                col.items.forEach((item) => {
                    let keyCode = unassignedKey

                    if (filterValue?.groups?.group_key === "assignee") {
                        keyCode = item.user || unassignedKey;
                    } else if (filterValue?.groups?.group_key === "projects") {
                        keyCode = item.project_name || unassignedKey;
                    } else if (filterValue?.groups?.group_key === "epic") {
                        keyCode = item.parent || unassignedKey;
                    }
                    if (!groupedByUserThenStatus[keyCode]) {
                        groupedByUserThenStatus[keyCode] = {
                            name: keyCode,
                            image_url: item?.image_url || null,
                            id: item?.user_code || null,
                            feature_issue_key: item?.feature_issue_key || null,
                            groups: {},
                        };
                    }
                    const status = item.status_name || col.name || unassignedKey;
                    if (!groupedByUserThenStatus[keyCode].groups[status]) {
                        groupedByUserThenStatus[keyCode].groups[status] = { name: status, items: [] };
                    }
                    groupedByUserThenStatus[keyCode].groups[status].items.push(item);
                });
            });
            Object.values(groupedByUserThenStatus).forEach((userGroup) => {
                fillAndOrderGroups(userGroup, allGroupNames);
            });
            const keys = Object.keys(groupedByUserThenStatus);
            const sortedKeys = keys.filter((k) => k !== unassignedKey).sort();
            if (keys.includes(unassignedKey)) {
                sortedKeys.push(unassignedKey);
            }
            const reordered = {};
            sortedKeys.forEach((key) => {
                reordered[key] = groupedByUserThenStatus[key];
            });

            return reordered;
        } else {
            return {
                all: {
                    name: "All Tasks",
                    id: "all",
                    groups: Object.entries(columns).reduce((acc, [statusKey, statusCol]) => {
                        acc[statusKey] = {
                            name: statusCol.name || statusKey,
                            items: statusCol.items || [],
                        };
                        return acc;
                    }, {}),
                },
            };
        }
    }, [columns, filterValue]);


    const handleToggleSection = (key) => {
        setExpandedSections((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };


    const getEpicList = async () => {
        try {
            if (filterValue?.projects?.length !== 0) {
                const projectId = _.pluck(filterValue?.projects, "project_id")?.toString()
                const payload = {
                    "project_ids": filterValue?.projects?.length > 0 ? projectId.toString() : ''
                }
                const res = await services.getInvokeRemote('POST', Urls('GETEPIC'), payload);
                if (filterValue?.projects?.length === 0) {
                    setOptions((prev) => ({ ...prev, epicList: [] }))
                } else {
                    setOptions((prev) => ({ ...prev, epicList: _.sortBy(res.body.data.epic_data, 'issue_summary') }))
                }
            }
        } catch (error) {
            console.error("error:::::", error)
        }
    }

    return useMemo(
        () => ({
            columns, sensors, activeId, activeTask, handleDragStart, handleDragEnd, selectMode, setSelectMode, selectedCardIds, setSelectedCardIds, moveSelectedCardsToColumn, option, filterValue, setFilterValue, loader, normalizedColumns, expandedSections, handleToggleSection, getKanbanDetails, getEpicList, customCollisionDetection
        }), [columns, sensors, activeId, activeTask, handleDragStart, handleDragEnd, selectMode, setSelectMode, selectedCardIds, setSelectedCardIds, moveSelectedCardsToColumn, option, filterValue, setFilterValue, loader, normalizedColumns, expandedSections, handleToggleSection, getKanbanDetails, getEpicList, customCollisionDetection]
    );
};


